import React, { useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stack,
  Divider,
  Link,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { customerService } from '../../services/customer';
import { Customer } from '../../types/customer';

const CustomerSearchPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [keyword, setKeyword] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    const trimmed = keyword.trim();
    if (!trimmed) {
      setError('客先名を入力してください');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const results = await customerService.searchByName(trimmed);
      setCustomers(results);
      setHasSearched(true);
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      setError(apiError || '検索に失敗しました');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const isAllAddressEmpty = (customer: Customer) =>
    !customer.Address && !customer.Address2 && !customer.Address3;

  const buildMapUrl = (address: string) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

  const renderAddress = (value: string | null | undefined) => {
    if (!value) return '—';
    return (
      <Link
        href={buildMapUrl(value)}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
      >
        {value}
      </Link>
    );
  };

  const isSubmitDisabled = loading || keyword.trim().length === 0;

  const sortedCustomers = useMemo(
    () =>
      [...customers].sort(
        (a, b) => Number(isAllAddressEmpty(a)) - Number(isAllAddressEmpty(b))
      ),
    [customers]
  );

  const renderMobileCards = () => (
    <Stack spacing={2}>
      {sortedCustomers.map((customer) => (
        <Paper key={customer.ID} variant="outlined" sx={{ p: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', wordBreak: 'break-word', mb: 1 }}
          >
            {customer.Customer_Name}
          </Typography>

          <Divider sx={{ mb: 1 }} />

          {isAllAddressEmpty(customer) ? (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontStyle: 'italic' }}
            >
              住所: 未入力
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              <AddressLine label="住所" value={customer.Address} />
              <AddressLine label="住所2" value={customer.Address2} />
              <AddressLine label="住所3" value={customer.Address3} />
            </Stack>
          )}
        </Paper>
      ))}
    </Stack>
  );

  const renderDesktopTable = () => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>客先名</TableCell>
            <TableCell>住所</TableCell>
            <TableCell>住所2</TableCell>
            <TableCell>住所3</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedCustomers.map((customer) => (
            <TableRow key={customer.ID} hover>
              <TableCell sx={{ wordBreak: 'break-word' }}>{customer.Customer_Name}</TableCell>
              {isAllAddressEmpty(customer) ? (
                <TableCell colSpan={3} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  未入力
                </TableCell>
              ) : (
                <>
                  <TableCell>{renderAddress(customer.Address)}</TableCell>
                  <TableCell>{renderAddress(customer.Address2)}</TableCell>
                  <TableCell>{renderAddress(customer.Address3)}</TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container>
      <Box sx={{ my: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          客先検索
        </Typography>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <TextField
              fullWidth
              label="客先名"
              placeholder="客先名で検索（部分一致）"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={handleKeyDown}
              InputProps={{
                endAdornment: keyword ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setKeyword('')} edge="end">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={isSubmitDisabled}
              sx={{ minWidth: { sm: 120 } }}
            >
              検索
            </Button>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : hasSearched && customers.length === 0 && !error ? (
          <Alert severity="info">該当する客先が見つかりませんでした。</Alert>
        ) : customers.length > 0 ? (
          <>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <SearchIcon fontSize="small" />
              検索結果: {customers.length}件
            </Typography>

            {isMobile ? renderMobileCards() : renderDesktopTable()}
          </>
        ) : null}
      </Box>
    </Container>
  );
};

interface AddressLineProps {
  label: string;
  value: string | null | undefined;
}

const AddressLine: React.FC<AddressLineProps> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, flexShrink: 0 }}>
      {label}
    </Typography>
    {value ? (
      <Link
        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        variant="body2"
        sx={{ wordBreak: 'break-word' }}
      >
        {value}
      </Link>
    ) : (
      <Typography variant="body2" sx={{ color: 'text.disabled' }}>
        —
      </Typography>
    )}
  </Box>
);

export default CustomerSearchPage;
